import { getLocale } from 'src/services/storage';

const arabicMonths = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
] as const;

function dateLocale(): string {
  return getLocale() === 'ar' ? 'ar-SA' : 'en-GB';
}

export function formatDate(input: string | number | undefined): string {
  if (!input) return '';
  return new Date(input).toLocaleDateString(dateLocale(), {
    calendar: 'gregory',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatDateTime(input: string | number | undefined): string {
  if (!input) return '';
  const date = new Date(input);
  const datePart = date.toLocaleDateString('en-GB', {
    calendar: 'gregory',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${datePart} - ${timePart}`;
}

export function formatShortDate(input: string | number | undefined): string {
  if (!input) return '';
  return new Date(input).toLocaleDateString(dateLocale(), {
    calendar: 'gregory',
    day: 'numeric',
    month: 'long',
  });
}

export function dateToKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function formatDateKey(key: string): string {
  const [year, month, day] = key.split('-').map(Number);
  if (getLocale() === 'ar') {
    // ‏ (RLM) keeps the day/month order intact when this string is concatenated with LTR content
    return `‏${day} ${arabicMonths[(month ?? 1) - 1] ?? ''}‏`;
  }
  return new Date(year!, (month ?? 1) - 1, day).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
  });
}

export function formatMonthName(year: number, month: number): string {
  if (getLocale() === 'ar') return arabicMonths[month] ?? '';
  return new Date(year, month, 1).toLocaleString('en-US', { month: 'long' });
}

export function formatPrice(price: number): string {
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export async function sha256Hex(input: string | undefined | null): Promise<string | undefined> {
  if (!input) return undefined;
  const normalized = input.trim().toLowerCase();
  if (!normalized || !globalThis.crypto?.subtle) return undefined;
  const bytes = new TextEncoder().encode(normalized);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function normalizePhone(dialCode: string, phone: string): string {
  return `${dialCode}${phone}`.replace(/\D/g, '');
}

// @note Arabic-Indic (U+0660–0669) and Eastern-Arabic-Indic (U+06F0–06F9) digits → ASCII 0–9.
// Used so users typing in Arabic keyboards get the same numeric payload as those typing in Latin.
export function toEnglishDigits(input: string): string {
  return input
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
}
